import { useMemo } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  MiniBars,
  PageHeader,
  SkeletonRows,
  StatTile,
  cmpDecimal,
  formatMoney,
  isZeroDecimal,
  mulDecimal,
  roundDecimalString,
} from '@biovet/ui';
import { Th } from './parts.js';
import { useResource, type DashboardResponse } from '../lib/api.js';
import type { Role } from '../lib/voucher-model.js';

export function DashboardScreen({
  userName,
  role,
  onOpenItem,
}: {
  userName: string;
  role: Role;
  onOpenItem: (module: string, item: string) => void;
}) {
  const { data, error, loading } = useResource<DashboardResponse>('/dashboard');

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const queue = useMemo(() => (data ? buildQueue(data, role) : null), [data, role]);
  const cash = useMemo(() => cashChart(data), [data]);

  if (error) {
    return (
      <>
        <PageHeader breadcrumb="Dashboard" title="Overview" description="" />
        <Callout tone="danger" title="The dashboard could not be built">
          {error}
        </Callout>
      </>
    );
  }

  const rate = data?.usdRate ?? null;

  return (
    <>
      <PageHeader
        breadcrumb={[
          formatDateLine(now),
          data?.fiscalYear ? `FY ${data.fiscalYear}` : null,
          data?.period && data.period !== `FY ${data.fiscalYear}` ? data.period : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        title={
          <>
            <span className="text-[var(--ink-2)]">{greeting},</span> {userName}
          </>
        }
        description={queue?.insight ?? 'Reading the book…'}
      />

      <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
        {(data?.kpis ?? PLACEHOLDER_KPIS).map((kpi) => (
          <Card key={kpi.key} className="bb-pad">
            <div className="flex items-center justify-between gap-2.5">
              <span className="bb-eyebrow">{kpi.label}</span>
              {}
              {kpi.deltaPct !== null && (
                <span
                  className={`bb-num rounded-[5px] px-1.5 py-0.5 text-[11px] font-medium ${
                    kpi.deltaPct.startsWith('-')
                      ? 'bg-[var(--debit-soft)] text-[var(--debit)]'
                      : 'bg-[var(--accent-soft)] text-[var(--credit)]'
                  }`}
                >
                  {kpi.deltaPct}%
                </span>
              )}
            </div>
            <div className="flex items-baseline gap-1.5 pt-3.5 pb-1">
              <span className="bb-num text-[11.5px] font-medium text-[var(--ink-3)]">PKR</span>
              <span className="bb-num text-[26px] font-semibold tracking-tight tabular-nums">
                {loading ? '—' : formatMoney(kpi.valueBase, { decimals: 0 })}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between gap-2 border-t border-[var(--line-2)] pt-2">
              <span className="bb-num text-[11.5px] text-[var(--ink-2)]">
                {loading ? '—' : `≈ USD ${toUsd(kpi.valueBase, rate)}`}
              </span>
              <span className="text-[11px] text-[var(--ink-3)]">{kpi.foot}</span>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid gap-3.5 pt-3.5 lg:grid-cols-2">
        <Card flush>
          <CardHeader
            title="Brokerage line"
            subtitle="Commission agent · company owns no goods"
            actions={
              <Button size="sm" onClick={() => onOpenItem('brokerage', 'deal-register')}>
                Deal register
              </Button>
            }
          />
          <CardBody className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatTile
              label="Deal value MTD"
              value={compact(data?.brokerage.deal_value_mtd)}
              hint={`memo only · ${plural(data?.brokerage.deal_count_mtd, 'deal')}`}
            />
            <StatTile
              label="Commission"
              value={<Tinted value={compact(data?.brokerage.commission_mtd)} tone="credit" />}
              hint="income posted"
            />
            <StatTile
              label="Receivable"
              value={compact(data?.brokerage.commission_receivable)}
              hint={plural(data?.brokerage.commission_invoice_count, 'invoice')}
            />
            <StatTile
              label="Unsettled"
              value={<Tinted value={compact(data?.brokerage.unsettled)} tone="warn" />}
              hint="B owes A"
            />
          </CardBody>
        </Card>

        <Card flush>
          <CardHeader
            title="Import & distribution line"
            subtitle="Owns stock · full purchase, inventory and sales"
            actions={
              data && !isZeroDecimal(data.trading.net_sales_mtd) ? (
                <Badge tone="ok">
                  {marginPct(data.trading.margin_mtd, data.trading.net_sales_mtd)} GM
                </Badge>
              ) : undefined
            }
          />
          <CardBody className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatTile
              label="Net sales MTD"
              value={compact(data?.trading.net_sales_mtd)}
              hint={plural(data?.trading.invoice_count_mtd, 'invoice')}
            />
            <StatTile
              label="Stock value"
              value={compact(data?.trading.stock_value)}
              hint={plural(data?.trading.warehouse_count, 'warehouse')}
            />
            <StatTile
              label="In transit"
              value={compact(data?.trading.in_transit)}
              hint={plural(data?.trading.in_transit_lots, 'lot')}
            />
            <StatTile
              label="USD payable"
              value={
                <Tinted
                  value={
                    loading ? '—' : formatMoney(data?.trading.usd_payable ?? '0', { decimals: 0 })
                  }
                  tone="debit"
                />
              }
              hint="unhedged"
            />
          </CardBody>
        </Card>
      </div>

      <div className="grid gap-3.5 pt-3.5 lg:grid-cols-[1.55fr_1fr]">
        <Card flush>
          <CardHeader
            title="Cash & bank movement"
            subtitle="Last 12 weeks · receipts vs payments · internal transfers excluded"
            actions={
              cash.empty ? undefined : (
                <div className="flex items-center gap-3.5 text-[11.5px] text-[var(--ink-2)]">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-[2px] bg-[var(--accent)]" />
                    Receipts
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-[2px] bg-[var(--line)]" />
                    Payments
                  </span>
                </div>
              )
            }
          />
          <CardBody>
            {loading ? (
              <SkeletonRows rows={4} />
            ) : cash.empty ? (
              <p className="py-10 text-center text-[12.5px] text-[var(--ink-2)]">
                No cash or bank movement in the last 12 weeks.
                <span className="block pt-1 text-[11.5px] text-[var(--ink-3)]">
                  Receipts and payments appear here once a cash or bank voucher posts. Transfers
                  between your own tills and accounts are left out — nothing entered or left the
                  business.
                </span>
              </p>
            ) : (
              <>
                {}
                <div className="flex justify-end pb-1 text-[10.5px] text-[var(--ink-3)]">
                  peak {formatMoney(cash.peak, { decimals: 0 })} in a week
                </div>
                <MiniBars series={cash.series} height={168} labels={cash.labels} />
                <div className="bb-num flex justify-between pt-2 text-[10px] text-[var(--ink-3)]">
                  {cash.series.map((_, i) => (
                    <span key={i}>W{i + 1}</span>
                  ))}
                </div>
              </>
            )}
          </CardBody>
        </Card>

        <Card flush>
          <CardHeader
            title={queue?.title ?? 'Your queue'}
            subtitle={queue?.subtitle ?? ''}
            actions={
              queue && queue.count > 0 ? (
                <Badge tone="warn">{queue.count}</Badge>
              ) : (
                <Badge tone="ok">Clear</Badge>
              )
            }
          />
          {loading ? (
            <SkeletonRows rows={4} />
          ) : !queue || queue.items.length === 0 ? (
            <CardBody>
              <p className="text-[12.5px] text-[var(--ink-2)]">
                Nothing is waiting on you. No drafts, no approvals pending, no unmatched bank lines
                and nothing overdue.
              </p>
            </CardBody>
          ) : (
            <div className="flex flex-col">
              {queue.items.map((q) => (
                <button
                  key={q.title}
                  type="button"
                  onClick={() => onOpenItem(q.module, q.item)}
                  className="flex cursor-pointer items-center gap-2.5 border-b border-[var(--line-2)] bg-transparent px-[var(--pad)] py-2.5 text-left hover:bg-[var(--surface-2)]"
                >
                  <span
                    aria-hidden="true"
                    className={`size-[7px] shrink-0 rounded-full ${QUEUE_DOT[q.tone]}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium">{q.title}</div>
                    <div className="truncate text-[11.5px] text-[var(--ink-3)]">{q.meta}</div>
                  </div>
                  <span className="bb-num text-[12.5px] font-medium tabular-nums">{q.amount}</span>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid gap-3.5 pt-3.5 xl:grid-cols-3">
        <Card flush className="xl:col-span-2">
          <CardHeader
            title="Recent postings"
            actions={
              <Button variant="link" size="sm" onClick={() => onOpenItem('finance', 'gl')}>
                General ledger →
              </Button>
            }
          />
          {loading ? (
            <SkeletonRows rows={6} />
          ) : (data?.recent.length ?? 0) === 0 ? (
            <CardBody>
              <p className="text-[12.5px] text-[var(--ink-2)]">
                Nothing has been entered yet. The first voucher will appear here.
              </p>
            </CardBody>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[var(--surface-2)]">
                    <Th width="140px">Voucher</Th>
                    <Th>Narration</Th>
                    <Th width="110px">Date</Th>
                    <Th align="right" width="140px">
                      Amount
                    </Th>
                    <Th align="right" width="110px">
                      Status
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.recent ?? []).map((p) => (
                    <tr
                      key={p.voucherNo}
                      className="bb-row-h border-b border-[var(--line-2)] hover:bg-[var(--surface-2)]"
                    >
                      <td className="bb-num px-[var(--pad)] text-[12.5px] font-medium">
                        {p.voucherNo}
                      </td>
                      <td className="max-w-[320px] truncate px-2.5 text-[13px] text-[var(--ink-2)]">
                        {p.narration ?? p.partyName ?? '—'}
                      </td>
                      <td className="bb-num px-2.5 text-[12.5px] text-[var(--ink-2)]">
                        {p.voucherDate}
                      </td>
                      <td className="bb-num px-2.5 text-right text-[12.5px] tabular-nums">
                        {formatMoney(p.totalBase, { decimals: 0 })}
                      </td>
                      <td className="px-[var(--pad)] text-right">
                        <Badge status={p.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="flex flex-col gap-3.5">
          <Card className="bb-pad">
            <div className="flex items-center justify-between pb-3">
              <h2 className="m-0 text-[14px] font-semibold tracking-tight">Trial balance check</h2>
              {}
              {data && isZeroDecimal(data.trialBalance.difference) ? (
                <Badge tone="ok" dot>
                  Balanced
                </Badge>
              ) : data ? (
                <Badge tone="bad" dot>
                  Out by {formatMoney(data.trialBalance.difference)}
                </Badge>
              ) : null}
            </div>
            <div className="flex flex-col gap-2.5">
              <Line label="Total debits" value={money(data?.trialBalance.debits)} />
              <Line label="Total credits" value={money(data?.trialBalance.credits)} />
              <div className="mt-0.5 h-1.5 overflow-hidden rounded-md bg-[var(--line)]">
                <div
                  className={`h-full w-full ${
                    data && isZeroDecimal(data.trialBalance.difference)
                      ? 'bg-[var(--credit)]'
                      : 'bg-[var(--debit)]'
                  }`}
                />
              </div>
              <div className="flex justify-between pt-0.5 text-[11.5px] text-[var(--ink-3)]">
                <span>Unposted vouchers</span>
                <span className="bb-num">{data?.trialBalance.unposted ?? 0}</span>
              </div>
            </div>
            <Button
              fullWidth
              size="sm"
              className="mt-3.5"
              onClick={() => onOpenItem('finance', 'tb')}
            >
              Open trial balance
            </Button>
          </Card>

          <Card className="bb-pad flex-1">
            <h2 className="m-0 pb-3 text-[14px] font-semibold tracking-tight">Stock at risk</h2>
            {(data?.stockAtRisk.length ?? 0) === 0 ? (
              <p className="text-[12px] text-[var(--ink-2)]">
                No batch is within ninety days of expiry.
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                {(data?.stockAtRisk ?? []).map((s) => (
                  <div key={`${s.sku}-${s.batchNo}-${s.warehouseName}`}>
                    <div className="flex items-baseline justify-between gap-2 pb-1.5">
                      <span className="truncate text-[12.5px] font-medium">{s.productName}</span>
                      <span className="bb-num shrink-0 text-[11.5px] text-[var(--ink-3)]">
                        {formatMoney(s.balanceQty, { decimals: 0 })}
                      </span>
                    </div>
                    <div className="h-[5px] overflow-hidden rounded-md bg-[var(--line-2)]">
                      {}
                      <div
                        className={`h-full rounded-md ${
                          (s.daysToExpiry ?? 999) <= 30 ? 'bg-[var(--debit)]' : 'bg-[var(--warn)]'
                        }`}
                        style={{ width: `${riskWidth(s.daysToExpiry)}%` }}
                      />
                    </div>
                    <div className="pt-1 text-[11px] text-[var(--ink-3)]">
                      {s.warehouseName} ·{' '}
                      {s.daysToExpiry === null
                        ? 'no expiry'
                        : s.daysToExpiry < 0
                          ? `expired ${-s.daysToExpiry} days ago`
                          : `expires in ${s.daysToExpiry} days`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}


interface QueueEntry {
  title: string;
  meta: string;
  amount: string;
  tone: 'ok' | 'warn' | 'bad' | 'mute';
  module: string;
  item: string;
}

function buildQueue(data: DashboardResponse, role: Role) {
  const q = data.queue;
  const all: Record<string, QueueEntry> = {
    drafts: {
      title: `${q.drafts} draft voucher${q.drafts === 1 ? '' : 's'}`,
      meta: 'entered, not posted',
      amount: String(q.drafts),
      tone: 'warn',
      module: 'finance',
      item: 'jv',
    },
    approvals: {
      title: `${q.pending_approval} awaiting approval`,
      meta: 'maker–checker',
      amount: String(q.pending_approval),
      tone: 'bad',
      module: 'finance',
      item: 'jv',
    },
    bank: {
      title: `${q.unmatched_bank} unmatched bank line${q.unmatched_bank === 1 ? '' : 's'}`,
      meta: 'statement vs ledger',
      amount: String(q.unmatched_bank),
      tone: 'warn',
      module: 'finance',
      item: 'recon',
    },
    overdue: {
      title: `${q.overdue_invoices} overdue invoice${q.overdue_invoices === 1 ? '' : 's'}`,
      meta: 'past due date',
      amount: formatMoney(q.overdue_base, { decimals: 0 }),
      tone: 'bad',
      module: 'sales',
      item: 'invoice',
    },
    expiry: {
      title: `${q.expiring_batches} batch${q.expiring_batches === 1 ? '' : 'es'} near expiry`,
      meta: 'within 30 days',
      amount: String(q.expiring_batches),
      tone: 'bad',
      module: 'inventory',
      item: 'batch',
    },
    settlement: {
      title: `${q.unsettled_deals} deal${q.unsettled_deals === 1 ? '' : 's'} unsettled`,
      meta: 'B owes A — blocks commission',
      amount: formatMoney(data.brokerage.unsettled, { decimals: 0 }),
      tone: 'warn',
      module: 'brokerage',
      item: 'settlement',
    },
  };

  const order: Record<Role, Array<{ key: keyof typeof all; count: number }>> = {
    accountant: [
      { key: 'drafts', count: q.drafts },
      { key: 'bank', count: q.unmatched_bank },
      { key: 'overdue', count: q.overdue_invoices },
      { key: 'expiry', count: q.expiring_batches },
    ],
    manager: [
      { key: 'approvals', count: q.pending_approval },
      { key: 'overdue', count: q.overdue_invoices },
      { key: 'drafts', count: q.drafts },
      { key: 'settlement', count: q.unsettled_deals },
    ],
    owner: [
      { key: 'overdue', count: q.overdue_invoices },
      { key: 'settlement', count: q.unsettled_deals },
      { key: 'approvals', count: q.pending_approval },
      { key: 'expiry', count: q.expiring_batches },
    ],
  };

  const chosen = order[role] ?? order.accountant;
  const items = chosen.filter((c) => c.count > 0).map((c) => all[c.key] as QueueEntry);
  const count = chosen.reduce((n, c) => n + c.count, 0);

  const titles: Record<Role, { title: string; subtitle: string }> = {
    accountant: { title: 'Your queue', subtitle: 'Entries and reconciliation waiting on you' },
    manager: { title: 'Awaiting you', subtitle: 'Approvals and exposures on your desk' },
    owner: { title: 'Position', subtitle: 'What is exposed and what is stuck' },
  };

  return {
    ...(titles[role] ?? titles.accountant),
    count,
    items,
    insight: insightFor(data, items.length),
  };
}

function insightFor(data: DashboardResponse, queued: number): string {
  const nothingPosted = isZeroDecimal(data.trialBalance.debits) && data.recent.length === 0;
  if (nothingPosted) {
    return 'Nothing has been posted to this book yet. The figures below will fill in as vouchers, invoices and stock movements are entered.';
  }

  const parts: string[] = [];
  if (!isZeroDecimal(data.trading.net_sales_mtd)) {
    parts.push(
      `Net sales of ${formatMoney(data.trading.net_sales_mtd, { decimals: 0 })} this month at ${marginPct(data.trading.margin_mtd, data.trading.net_sales_mtd)} margin`,
    );
  }
  if (!isZeroDecimal(data.brokerage.commission_mtd)) {
    parts.push(
      `brokerage contributed ${formatMoney(data.brokerage.commission_mtd, { decimals: 0 })} on none of the working capital`,
    );
  }
  if (!isZeroDecimal(data.brokerage.unsettled)) {
    parts.push(
      `${formatMoney(data.brokerage.unsettled, { decimals: 0 })} of buyer settlements remain open`,
    );
  }
  if (parts.length === 0) {
    return queued > 0
      ? 'The ledger is balanced. The queue on the right is what is waiting on you.'
      : 'The ledger is balanced and nothing is waiting on you.';
  }
  return `${parts.join(', and ')}.`;
}


const QUEUE_DOT: Record<'ok' | 'warn' | 'bad' | 'mute', string> = {
  ok: 'bg-[var(--credit)]',
  warn: 'bg-[var(--warn)]',
  bad: 'bg-[var(--debit)]',
  mute: 'bg-[var(--ink-3)]',
};

const PLACEHOLDER_KPIS: DashboardResponse['kpis'] = [
  { key: 'cash', label: 'Cash & bank', valueBase: '0', deltaPct: null, foot: '—' },
  { key: 'receivables', label: 'Receivables', valueBase: '0', deltaPct: null, foot: '—' },
  { key: 'payables', label: 'Payables', valueBase: '0', deltaPct: null, foot: '—' },
  { key: 'profit', label: 'Profit before tax', valueBase: '0', deltaPct: null, foot: '—' },
];

function plural(n: number | undefined, noun: string): string {
  const count = n ?? 0;
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function money(value: string | undefined): string {
  return value === undefined ? '—' : formatMoney(value, { decimals: 0 });
}

function compact(value: string | undefined): string {
  if (value === undefined) return '—';
  const negative = value.startsWith('-');
  const abs = negative ? value.slice(1) : value;
  const whole = abs.split('.')[0] ?? '0';
  const sign = negative ? '-' : '';

  if (whole.length > 9) return `${sign}${scaled(abs, '0.000000001')}b`;
  if (whole.length > 6) return `${sign}${scaled(abs, '0.000001')}m`;
  if (whole.length > 3) return `${sign}${scaled(abs, '0.001')}k`;
  return `${sign}${formatMoney(abs, { decimals: 0 })}`;
}

function scaled(value: string, factor: string): string {
  return roundDecimalString(mulDecimal(value, factor), 2);
}

function marginPct(margin: string | undefined, sales: string | undefined): string {
  if (!margin || !sales || isZeroDecimal(sales)) return '—';
  return `${roundDecimalString(mulDecimal(divideApprox(margin, sales), '100'), 1)}%`;
}

function divideApprox(numerator: string, denominator: string): string {
  const n = Number(numerator);
  const d = Number(denominator);
  if (!Number.isFinite(n) || !Number.isFinite(d) || d === 0) return '0';
  return (n / d).toFixed(6);
}

function toUsd(base: string, rate: string | null): string {
  if (!rate || Number(rate) === 0) return '—';
  return formatMoney(divideApprox(base, rate), { decimals: 0 });
}

function cashChart(data: DashboardResponse | undefined) {
  const weeks = data?.cashBars ?? [];
  const empty = weeks.every((b) => isZeroDecimal(b.receipts) && isZeroDecimal(b.payments));
  const peak = weeks
    .flatMap((b) => [b.receipts, b.payments])
    .reduce((high, v) => (cmpDecimal(v, high) > 0 ? v : high), '0');
  return {
    empty,
    peak,
    series: weeks.map((b) => [Number(b.receipts), Number(b.payments)] as [number, number]),
    labels: weeks.map(
      (b) =>
        `Week of ${b.week} · in ${formatMoney(b.receipts, { decimals: 0 })} / out ${formatMoney(b.payments, { decimals: 0 })}`,
    ),
  };
}

function riskWidth(days: number | null): number {
  if (days === null) return 0;
  if (days <= 0) return 100;
  return Math.max(4, Math.round(((90 - Math.min(days, 90)) / 90) * 100));
}

function Tinted({ value, tone }: { value: string; tone?: 'credit' | 'debit' | 'warn' }) {
  const colour =
    tone === 'credit'
      ? 'text-[var(--credit)]'
      : tone === 'debit'
        ? 'text-[var(--debit)]'
        : tone === 'warn'
          ? 'text-[var(--warn)]'
          : undefined;
  return <span className={colour}>{value}</span>;
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-[12.5px]">
      <span className="text-[var(--ink-2)]">{label}</span>
      <span className="bb-num font-medium tabular-nums">{value}</span>
    </div>
  );
}

function formatDateLine(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: 'Asia/Karachi',
  }).format(date);
}
